/*
 * DOM helpers with the exact conventions the legacy call sites use: a bare
 * identifier means an element id, anything else is a selector; a "target" may
 * be a selector, an element, a list of elements or a jQuery object.
 */

/** Anything that names one or more elements. */
export type Target = string | Element | ArrayLike<Element> | Iterable<Element> | { toArray(): Element[] } | null | undefined;

const bareId = /^[A-Za-z][\w-]*$/;

/** '#Foo' for a bare identifier, the selector itself otherwise. */
export function selectorOf(target: string): string {
    return bareId.test(target) ? `#${target}` : target;
}

/** First element a target names, or null. */
export function one(target: Target): Element | null {
    if (!target) return null;
    if (typeof target === 'string') return document.querySelector(selectorOf(target));
    if (target instanceof Element) return target;
    const list = all(target);
    return list[0] ?? null;
}

/** Every element a target names. */
export function all(target: Target): Element[] {
    if (!target) return [];
    if (typeof target === 'string') return Array.from(document.querySelectorAll(selectorOf(target)));
    if (target instanceof Element) return [target];
    if (typeof (target as { toArray?: unknown }).toArray === 'function') {
        return (target as { toArray(): Element[] }).toArray();
    }
    if (typeof (target as ArrayLike<Element>).length === 'number') {
        return Array.from(target as ArrayLike<Element>);
    }
    return Array.from(target as Iterable<Element>);
}

export function show(target: Target): void {
    for (const el of all(target)) el.classList.remove('d-none');
}

export function hide(target: Target): void {
    for (const el of all(target)) el.classList.add('d-none');
}

export function toggleBusy(target: Target, busy: boolean): void {
    if (busy) show(target); else hide(target);
}

export type SwapMode = 'replace' | 'append' | 'before' | 'after' | 'replaceWith';

/**
 * Put server-rendered HTML into the page the way jQuery's .html() did --
 * including running the inline <script> blocks it contains. innerHTML alone
 * leaves them inert, and several partials still carry their own initialisers.
 * Returns the elements that were inserted (for 'replaceWith', the replacements).
 */
export function swapHtml(target: Target, html: string, mode: SwapMode = 'replace'): Element[] {
    const container = one(target);
    if (!container) return [];

    const template = document.createElement('template');
    template.innerHTML = html;
    const fragment = template.content;
    const scripts = Array.from(fragment.querySelectorAll('script'));
    for (const script of scripts) script.remove();         // re-added executable below
    const inserted = Array.from(fragment.children);

    switch (mode) {
        case 'append':
            container.appendChild(fragment);
            break;
        case 'before':
            container.before(fragment);
            break;
        case 'after':
            container.after(fragment);
            break;
        case 'replaceWith':
            container.replaceWith(fragment);
            break;
        default:
            container.replaceChildren(fragment);
            break;
    }

    const host = mode === 'replaceWith' ? (inserted[0]?.parentElement ?? document.body) : container;
    for (const script of scripts) {
        const live = document.createElement('script');
        for (const attr of Array.from(script.attributes)) live.setAttribute(attr.name, attr.value);
        live.textContent = script.textContent;
        host.appendChild(live);
        live.remove();                                      // executed on insertion; keep the tree clean
    }
    // Enhancers (popovers, datalists, ...) run over what arrived; see core/enhance.ts.
    host.dispatchEvent(new CustomEvent('mcp:html-swapped', { bubbles: true, detail: { inserted, mode } }));
    return inserted;
}

/** Centred spinner placeholder; `mode` is a Bootstrap text colour suffix. */
export function setSpinner(target: Target, mode?: string): void {
    const el = one(target);
    if (!el) return;
    const colour = mode ? ` text-${mode}` : '';
    el.innerHTML = `<div class="row"><div class="col-1 col-offset-5 centered"><i class="fas fa-spinner fa-spin${colour}" style="margin:5em;"></i></div></div>`;
}

/** Bootstrap-class-free element factory: tag, classes, text. */
export function el<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
}

/**
 * Escape operator- or server-supplied text for the places a confirm dialog or
 * a toast has to compose markup around it. One definition: three areas had
 * grown their own identical copy.
 */
export function escapeHtml(text: string | null | undefined): string {
    const node = document.createElement('div');
    node.textContent = text ?? '';
    return node.innerHTML;
}

/**
 * Same-tab file download: a synthesized <a download> keeps the server's
 * Content-Disposition filename even when the trigger sits inside a modal.
 */
export function download(url: string | null | undefined): boolean {
    if (!url) return false;
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    a.setAttribute('download', '');
    document.body.appendChild(a);
    try { a.click(); } finally { a.remove(); }
    return false;
}

/** Nearest ancestor (or self) matching a selector, typed. */
export function closest<T extends Element = HTMLElement>(from: Element | null, selector: string): T | null {
    return (from?.closest(selector) as T | null) ?? null;
}

/** Value of a form control by name inside a root. */
export function field<T extends HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement = HTMLInputElement>(root: ParentNode, name: string): T | null {
    return root.querySelector<T>(`[name="${CSS.escape(name)}"]`);
}
